<template>
    <div class="main-body">
        <h2>{{ $strings('spsite/transaction/summary/title') }}</h2>
        <h4>Overview</h4>
        <div class="cards">
            <el-row :gutter="16">
                <el-col :span="6">
                    <el-card shadow="hover" class="background-dark" v-loading="loadingSummary">
                        <span class="font-32 text-white">{{ formatNum(this.summaryData.count_total) }}</span>&nbsp;
                        <span class="font-12 vertical-top text-white">{{ $strings('word/total') }}</span>
                    </el-card>
                </el-col>
                <el-col :span="6">
                    <el-card shadow="hover" class="background-primary" v-loading="loadingSummary">
                        <span class="font-32 text-white">{{ formatNum(this.summaryData.count_scheduled) }}</span>&nbsp;
                        <span class="font-12 vertical-top text-white">{{
                                this.scheduledRate + ' ' + $strings('word/scheduled')
                            }}</span>
                    </el-card>
                </el-col>
                <el-col :span="6">
                    <el-card shadow="hover" class="background-success" v-loading="loadingSummary">
                        <span class="font-32 text-white">{{ formatNum(this.summaryData.count_delivered) }}</span>&nbsp;
                        <span class="font-12 vertical-top text-white">{{
                                this.deliverRate + ' ' + $strings('word/delivered')
                            }}</span>
                    </el-card>
                </el-col>
                <el-col :span="6">
                    <el-card shadow="hover" class="background-danger" v-loading="loadingSummary">
                        <span class="font-32 text-white">{{ formatNum(summaryErrorCount) }}</span>&nbsp;
                        <span class="font-12 vertical-top text-white">{{
                                this.errorRate + ' ' + $strings('word/error')
                            }}</span>
                    </el-card>
                </el-col>
            </el-row>
        </div>
        <h4>Filter</h4>
        <div class="action-bar">
            <div class="filter-wrapper">
                <el-form :inline="true" :model="filter" class="demo-form-inline" ref="filter">
                    <el-form-item label="">
                        <el-select v-model="filter.tz" size="small">
                            <el-option v-for="(val,idx) in options.tzs" :key="idx" :label="val.label"
                                       :value="val.value"></el-option>
                        </el-select>
                    </el-form-item>
                    <el-form-item label="" style="padding-top: 1px;">
                        <el-date-picker
                            class="max-width-200"
                            size="small"
                            format="MM-dd"
                            value-format="yyyy-MM-dd"
                            v-model="dateRange"
                            type="daterange"
                            range-separator="~"
                            start-placeholder="From"
                            end-placeholder="To">
                        </el-date-picker>
                    </el-form-item>
                    <el-form-item label="">
                        <FilterSp v-model="filter.sp_id" @load="onLoadSp" class="max-width-120"/>
                    </el-form-item>
                    <el-form-item label="">
                        <FilterCp v-model="filter.cp_id" @load="onLoadCp" class="max-width-120"/>
                    </el-form-item>
                    <el-form-item label="">
                        <el-select v-model="filter.country" :placeholder="$strings('word/country')" size="small"
                                   class="max-width-120">
                            <el-option v-for="(item, idx) in options.cts" :label="item.label" :value="item.value"
                                       :key="idx"></el-option>
                        </el-select>
                    </el-form-item>
                    <el-form-item style="margin-right: 0;">
                        <el-button @click="onFilter" size="small">{{
                                $strings('word/search')
                            }}
                        </el-button>
                        <el-button @click="onReset" size="small">{{ $strings('word/reset') }}</el-button>
                    </el-form-item>
                </el-form>
            </div>
        </div>
        <h4>Data</h4>
        <div>
            <el-table v-loading="loadingTable" border :data="tableData">
                <el-table-column prop="dt" :label="$strings('word/date')" width="100"></el-table-column>
                <el-table-column prop="sp_id" :label="$strings('word/sp_id')" width="100"></el-table-column>
                <el-table-column prop="sp_name" :label="$strings('word/sp_name')" width="100"></el-table-column>
                <el-table-column prop="cp_id" :label="$strings('word/cp_id')" width="100"></el-table-column>
                <el-table-column prop="cp_name" :label="$strings('word/cp_name')" width="100"></el-table-column>
                <el-table-column prop="country" :label="$strings('word/country')"></el-table-column>
                <el-table-column prop="count_sum" :label="$strings('word/count')"></el-table-column>
                <el-table-column prop="count_scheduled" :label="$strings('word/count_scheduled')"
                                 width="100"></el-table-column>
                <el-table-column prop="count_delivered" :label="$strings('word/count_delivered')"
                                 width="100"></el-table-column>
                <el-table-column prop="count_delivered" :label="$strings('word/per_success')"
                                 width="100">
                    <template slot-scope="scope">
                        <span v-if="scope.row.count_sum > 0 && scope.row.count_delivered >0">
                            {{ (scope.row.count_delivered * 100 / scope.row.count_sum).toFixed(4) + '%' }}
                        </span>
                        <span v-else>0%</span>
                    </template>
                </el-table-column>
                <el-table-column prop="count_delivered" :label="$strings('word/per_fail')"
                                 width="100">
                    <template slot-scope="scope">
                        <span
                            v-if="scope.row.count_sum > 0 && scope.row.count_delivered > 0 && scope.row.count_sum !== scope.row.count_delivered">
                            {{
                                ((scope.row.count_sum - scope.row.count_delivered) * 100 / scope.row.count_sum).toFixed(4) + '%'
                            }}
                        </span>
                        <span v-else>0%</span>
                    </template>
                </el-table-column>
                <el-table-column prop="count_expired" :label="$strings('word/count_expired')"
                                 width="100"></el-table-column>
                <el-table-column prop="count_accepted" :label="$strings('word/count_accepted')"
                                 width="100"></el-table-column>
                <el-table-column prop="count_deleted" :label="$strings('word/count_deleted')"
                                 width="100"></el-table-column>
                <el-table-column prop="count_enroute" :label="$strings('word/count_enroute')"
                                 width="100"></el-table-column>

                <el-table-column prop="count_invalid" :label="$strings('word/count_invalid')"
                                 width="100"></el-table-column>
                <el-table-column prop="count_rejected" :label="$strings('word/count_rejected')"
                                 width="100"></el-table-column>

            </el-table>
        </div>
    </div>
</template>

<script>
import {SpSiteTransactionReportRead, SpSiteTransactionSummaryRead} from "@/api/spsite/api";
import {Timezones} from "@/utils/timezone";
import {DefaultDate} from "@/utils/time";
import {CTS, STORAGE_KEY_LOGIN_TZ} from "@/config/config";
import FilterSp from '@/components/filter/sp'
import FilterCp from '@/components/filter/cp'
import {GetItem} from "@/utils/storage";
import {ToReadNumber} from "@/utils/str";

let defaultTz = GetItem(STORAGE_KEY_LOGIN_TZ, 0) * 1;

export default {
    name: "spsite-transaction-summary",
    components: {FilterSp, FilterCp},
    data() {
        return {
            loadingTable: true,
            loadingSummary: true,
            loadingCp: true,
            loadingSp: true,
            tableData: [],
            showFilter: true,
            pagination: {total: 0, page: 1, size: 20},
            filter: {tz: defaultTz ? defaultTz : 0, date_start: '', date_end: ''},
            mapping: {cps: {}, sps: {}},
            dateRange: [DefaultDate(), DefaultDate()],
            summaryData: {
                "count_scheduled": 0,
                "count_enroute": 0,
                "count_delivered": 0,
                "count_expired": 0,
                "count_deleted": 0,
                "count_undeliverable": 0,
                "count_accepted": 0,
                "count_invalid": 0,
                "count_rejected": 0,
                "count_total": 0,
            },
            deliverRate: '0%',
            errorRate: '0%',
            scheduledRate: '0%',
            options: {
                tzs: Timezones,
                sps: [],
                cps: [],
                cts: CTS,
            },
        }
    },
    created() {
        this.prepareData();
        this.refreshTable();
    },
    computed: {
        summaryErrorCount() {
            return this.summaryData.count_total - this.summaryData.count_delivered - this.summaryData.count_scheduled;
        }
    },
    methods: {
        onLoadSp(sps) {
            this.options.sps = [];
            this.mapping.sps = {};
            sps.forEach(item => {
                this.options.sps.push({label: item.sp_name, value: item.sp_id});
                this.mapping.sps[item.sp_id] = item.sp_name;
            })
        },
        formatNum(num) {
            return ToReadNumber(num);
        },
        onLoadCp(cps) {
            this.mapping.cps = {};
            cps.forEach((item) => {
                this.mapping.cps[item.cp_id] = item.cp_name;
            });
        },
        onFilter() {
            this.refreshTable();
        },
        onReset() {
            this.filter = {};
            this.refreshTable();
        },
        prepareData() {

        },
        refreshTable() {
            if (this.dateRange.length > 0) {
                this.filter.date_start = this.dateRange[0];
                this.filter.date_end = this.dateRange[1];
            }
            this.loadingTable = true;
            SpSiteTransactionReportRead(this.filter).then((resp) => {
                this.pagination.total = resp.data.data.count;
                this.tableData = resp.data.data.count > 0 ? resp.data.data.rows : [];
            }).finally(() => {
                this.loadingTable = false;
            });
            this.loadingSummary = true;
            SpSiteTransactionSummaryRead(this.filter).then((resp) => {
                this.summaryData = resp.data.data;
                if (this.summaryData.count_delivered > 0 && this.summaryData.count_total > 0) {
                    this.deliverRate = (100 * this.summaryData.count_delivered / this.summaryData.count_total).toFixed(4) + '%';
                } else {
                    this.deliverRate = '0%';
                }
                if (this.summaryData.count_scheduled > 0 && this.summaryData.count_total > 0) {
                    this.scheduledRate = (100 * this.summaryData.count_scheduled / this.summaryData.count_total).toFixed(4) + '%';
                } else {
                    this.scheduledRate = '0%';
                }
                if (this.summaryErrorCount > 0 && this.summaryData.count_total > 0) {
                    this.errorRate = (100 * this.summaryErrorCount / (this.summaryData.count_total)).toFixed(4) + '%';
                } else {
                    this.errorRate = '0%';
                }
            }).finally(() => {
                this.loadingSummary = false;
            });
        }
    }
}
</script>

<style lang="scss">
.min-card .el-card__header {
    padding: 0.8em 1em;
}
</style>

<style scoped lang="scss">
.action-bar {
    display: flex;
    flex-direction: row;

    .filter-wrapper {
        display: flex;
        justify-content: flex-start;
        flex-grow: 1;
    }
}

.table-pagination {
    margin-top: 1em;
    display: flex;
    justify-content: flex-end;
}

.cards {
    margin-bottom: 1em;
}
</style>